import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BulkImport from "@/components/BulkImport";
import { toast } from "sonner";
import { Trash2, Mic, Upload, RefreshCw, Pencil, X } from "lucide-react";

export default function DictationAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", text: "", wpm: 80, voice: "alloy", language: "en", tts_model: "tts-1", generate_audio: true });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: "", text: "", wpm: 80, language: "en" });
  const [uploadFile, setUploadFile] = useState(null);

  const load = () =>
    Promise.all([
      api.get("/dictations?language=en").then((r) => r.data),
      api.get("/dictations?language=hi").then((r) => r.data),
    ]).then(([en, hi]) => setItems([...en, ...hi]));
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        // PATCH — text/title/wpm/language only. Audio stays; use Regen to refresh.
        await api.patch(`/admin/dictations/${editingId}`, {
          title: form.title, text: form.text, wpm: Number(form.wpm), language: form.language,
        });
        toast.success("Dictation updated (use Regen to refresh audio)");
      } else {
        await api.post("/admin/dictations", { ...form, wpm: Number(form.wpm) });
        toast.success(form.generate_audio ? "Dictation added & audio generated" : "Dictation added (no audio yet)");
      }
      setForm({ title: "", text: "", wpm: 80, voice: form.language === "hi" ? "nova" : "alloy", language: form.language, tts_model: form.tts_model, generate_audio: form.generate_audio });
      setEditingId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const editDictation = (d) => {
    setEditingId(d.id);
    setForm({
      title: d.title || "", text: d.text || "", wpm: d.wpm || 80,
      voice: d.language === "hi" ? "nova" : "alloy",
      language: d.language || "en",
      tts_model: d.tts_model || "tts-1", generate_audio: false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm({ title: "", text: "", wpm: 80, voice: "alloy", language: "en", tts_model: "tts-1", generate_audio: true });
  };

  // Auto-tune voice + quality when the admin flips languages.
  const setLanguage = (v) => {
    setForm((f) => ({
      ...f,
      language: v,
      voice: v === "hi" ? "nova" : (f.voice === "nova" ? "alloy" : f.voice),
      tts_model: v === "hi" ? "tts-1-hd" : (f.tts_model === "tts-1-hd" ? "tts-1" : f.tts_model),
    }));
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error("Choose an MP3 file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("title", uploadForm.title);
    fd.append("text", uploadForm.text);
    fd.append("wpm", String(uploadForm.wpm));
    fd.append("language", uploadForm.language || "en");
    fd.append("file", uploadFile);
    try {
      await api.post("/admin/dictations/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setUploadForm({ title: "", text: "", wpm: 80, language: uploadForm.language });
      setUploadFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this dictation?")) return;
    await api.delete(`/admin/dictations/${id}`);
    load();
  };

  const regen = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/dictations/${id}/regenerate-audio`);
      toast.success("Audio regenerated");
      load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={generate} className={`border ${editingId ? "border-brand-crimson bg-red-50/40" : "border-black/20"} p-6 space-y-4`} data-testid="admin-dictation-tts-form">
        {editingId && (
          <div className="flex items-center justify-between gap-3 -mx-6 -mt-6 mb-2 px-6 py-2 bg-brand-crimson text-white">
            <div className="text-[10px] uppercase tracking-widest font-bold">Editing existing dictation</div>
            <Button type="button" onClick={cancelEdit} variant="outline" size="sm" className="rounded-none h-7 px-2 border-white/50 text-white hover:bg-white/10 text-[10px] uppercase tracking-widest font-bold" data-testid="admin-dictation-cancel-edit-btn">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2 mb-2">
          <Mic className="w-4 h-4" />
          <div className="text-xs uppercase tracking-[0.3em] font-bold">Generate with OpenAI TTS</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold">Title</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="admin-dictation-title" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold">Text (max ~4000 chars)</Label>
          <Textarea required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-none mt-1 font-mono min-h-32" data-testid="admin-dictation-text" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">WPM</Label>
            <Input type="number" required value={form.wpm} onChange={(e) => setForm({ ...form, wpm: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="admin-dictation-wpm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">Voice</Label>
            <Select value={form.voice} onValueChange={(v) => setForm({ ...form, voice: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="admin-dictation-voice"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"].map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold">Language</Label>
          <Select value={form.language} onValueChange={setLanguage}>
            <SelectTrigger className="rounded-none mt-1" data-testid="admin-dictation-language"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English (Shorthand)</SelectItem>
              <SelectItem value="hi">हिंदी · Hindi Shorthand (आशु लिपिक)</SelectItem>
            </SelectContent>
          </Select>
          {form.language === "hi" && (
            <div className="mt-2 text-[11px] font-mono text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="admin-dictation-hindi-note">
              <b>Note:</b> OpenAI TTS reads Devanagari correctly but with an English accent. HD + Nova is the least-Anglicised combo. Prefer uploading a native-Hindi MP3 if you need a fully authentic voice.
            </div>
          )}
        </div>

        <div className="border border-black/10 bg-secondary/40 rounded p-3 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="admin-dictation-autogen-wrap">
            <input
              type="checkbox"
              checked={form.generate_audio}
              onChange={(e) => setForm({ ...form, generate_audio: e.target.checked })}
              className="w-4 h-4 accent-brand-gold"
              data-testid="admin-dictation-autogen-toggle"
            />
            <span className="text-xs uppercase tracking-[0.2em] font-bold">Auto-generate audio (OpenAI TTS)</span>
          </label>
          {form.generate_audio && (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Quality</Label>
              <Select value={form.tts_model} onValueChange={(v) => setForm({ ...form, tts_model: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="admin-dictation-model"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tts-1">tts-1 · Standard (faster, cheaper)</SelectItem>
                  <SelectItem value="tts-1-hd">tts-1-hd · HD (cleaner consonants, recommended for Hindi)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {!form.generate_audio && (
            <div className="text-[11px] font-mono text-muted-foreground">Dictation will be saved without audio; you can upload an MP3 later or use "Regenerate audio" from the list below.</div>
          )}
        </div>

        <Button type="submit" disabled={busy} className={`rounded-none w-full h-11 ${editingId ? "bg-brand-crimson hover:bg-brand-crimson/90" : "bg-[#0A2540] hover:bg-[#002FA7]"} text-white text-xs uppercase tracking-[0.2em] font-bold`} data-testid="admin-dictation-generate-btn">
          {busy ? (editingId ? "Updating…" : (form.generate_audio ? "Generating…" : "Saving…")) : (editingId ? "Update dictation" : (form.generate_audio ? "Save & generate audio" : "Save dictation"))}
        </Button>
      </form>

      <form onSubmit={upload} className="border border-black/20 p-6 space-y-4" data-testid="admin-dictation-upload-form">
        <div className="flex items-center gap-2 mb-2">
          <Upload className="w-4 h-4" />
          <div className="text-xs uppercase tracking-[0.3em] font-bold">Upload MP3</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold">Title</Label>
          <Input required value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="admin-upload-title" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold">Reference text (used for accuracy check)</Label>
          <Textarea required value={uploadForm.text} onChange={(e) => setUploadForm({ ...uploadForm, text: e.target.value })} className="rounded-none mt-1 font-mono min-h-24" data-testid="admin-upload-text" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">WPM label</Label>
            <Input type="number" required value={uploadForm.wpm} onChange={(e) => setUploadForm({ ...uploadForm, wpm: Number(e.target.value) })} className="rounded-none mt-1 font-mono" data-testid="admin-upload-wpm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">MP3 file</Label>
            <Input type="file" accept="audio/*" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="rounded-none mt-1 font-mono" data-testid="admin-upload-file" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold">Language</Label>
          <Select value={uploadForm.language} onValueChange={(v) => setUploadForm({ ...uploadForm, language: v })}>
            <SelectTrigger className="rounded-none mt-1" data-testid="admin-upload-language"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English (Shorthand)</SelectItem>
              <SelectItem value="hi">हिंदी · Hindi Shorthand (आशु लिपिक)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} className="rounded-none w-full h-11 bg-[#0A2540] hover:bg-[#002FA7] text-white text-xs uppercase tracking-[0.2em] font-bold" data-testid="admin-upload-submit-btn">
          {busy ? "Uploading…" : "Upload"}
        </Button>
      </form>

      <div className="lg:col-span-2">
        <BulkImport
          module="dictation"
          requiredFields={["title", "text"]}
          optionalFields={["wpm", "language"]}
          onImported={load}
        />
      </div>

      <div className="lg:col-span-2">
        <h3 className="text-lg font-black tracking-tight mb-3">Existing dictations</h3>
        <div className="border border-black/15 divide-y divide-black/10" data-testid="admin-dictations-list">
          {items.length === 0 && <div className="p-4 font-mono text-sm text-neutral-500">None yet.</div>}
          {items.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-bold" lang={d.language || "en"}>
                  {d.title}
                  <span className="text-xs font-mono text-neutral-500"> · {d.wpm} WPM · {d.audio_source} · {d.language === "hi" ? "हिंदी" : "EN"}</span>
                </div>
                <div className="text-xs font-mono text-neutral-500">{d.word_count} words</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => editDictation(d)} variant="outline" size="sm" className="rounded-none border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white" data-testid={`admin-dictation-edit-${d.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button onClick={() => regen(d.id)} variant="outline" size="sm" className="rounded-none border-black/20" data-testid={`admin-dictation-regen-${d.id}`}>
                  <RefreshCw className="w-3 h-3 mr-1" /> Regen
                </Button>
                <Button onClick={() => del(d.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid={`admin-dictation-delete-${d.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
